import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DollarSign } from "lucide-react";
import { format } from "date-fns";

const fmt = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PayoutHistory = () => {
  const { user } = useAuth();
  const [payouts, setPayouts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchPayouts = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data } = await supabase
      .from("commission_payouts")
      .select("*")
      .eq("salesperson_id", user.id)
      .order("created_at", { ascending: false });
    setPayouts(data || []);
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchPayouts(); }, [fetchPayouts]);

  const pendingTotal = payouts
    .filter((p) => p.status === "pending")
    .reduce((s, p) => s + p.amount_cents, 0);

  const paidTotal = payouts
    .filter((p) => p.status === "paid")
    .reduce((s, p) => s + p.amount_cents, 0);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Pending Payout</span>
              <DollarSign size={16} className="text-champagne" />
            </div>
            <p className="font-display text-2xl text-foreground">{fmt(pendingTotal)}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">Total Paid</span>
              <DollarSign size={16} className="text-primary" />
            </div>
            <p className="font-display text-2xl text-foreground">{fmt(paidTotal)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Payout list */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="font-display text-lg">Payout History</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {payouts.length === 0 ? (
            <p className="text-center text-muted-foreground py-12 text-sm">No payouts yet — commissions will appear here once processed.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Paid Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="text-sm">
                      {format(new Date(p.period_start), "MMM d")} – {format(new Date(p.period_end), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell className="font-medium">{fmt(p.amount_cents)}</TableCell>
                    <TableCell>
                      <Badge variant={p.status === "paid" ? "default" : "secondary"} className="text-xs capitalize">
                        {p.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {p.paid_at ? format(new Date(p.paid_at), "MMM d, yyyy") : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PayoutHistory;
